import React from 'react';
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FILTER_OPTIONS, FILTER_LABELS } from "@/types/filter-options";

interface UpdatedRelationshipsData {
  relationshipType?: string;
  travelAvailability?: string;
  maritalStatus?: string;
  sigilo_presenca?: string;
}

interface UpdatedRelationshipsSectionProps {
  data: UpdatedRelationshipsData;
  updateData: (field: string, value: string) => void;
  isOnboarding?: boolean;
  isEditing?: boolean;
}

// Função para obter o label de exibição
const getDisplayLabel = (value: string, labelMap: Record<string, string>): string => {
  if (!value) return 'Não informado';
  return labelMap[value] || value;
};

const UpdatedRelationshipsSection: React.FC<UpdatedRelationshipsSectionProps> = ({
  data,
  updateData,
  isOnboarding = false,
  isEditing = true
}) => {
  const labelClass = isOnboarding ? "text-white" : "text-white/90 text-sm";
  const selectClass = isOnboarding 
    ? "bg-white/5 border-white/10 text-white" 
    : "bg-black/20 border-white/20 text-white hover:bg-black/30 transition-colors";
  const contentClass = isOnboarding 
    ? "bg-black/90 border-white/20 text-white" 
    : "bg-black/95 border-white/20 backdrop-blur-xl";
  const itemClass = isOnboarding 
    ? "text-white hover:bg-white/10 hover:text-white focus:bg-white/10 focus:text-white" 
    : "text-white hover:bg-white/10 focus:bg-white/10";

  // Se não está editando e não é onboarding, mostrar apenas valores como texto
  if (!isEditing && !isOnboarding) {
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-white font-medium text-lg mb-2">Tipo de conexão</h3>
          {data.relationshipType ? (
            <p className="text-white/80">
              {getDisplayLabel(data.relationshipType, FILTER_LABELS.relationshipType)}
            </p>
          ) : (
            <p className="text-red-400">Por favor, completar o perfil.</p>
          )}
        </div>

        <div>
          <h3 className="text-white font-medium text-lg mb-2">Tem disponibilidade para viajar?</h3>
          {data.travelAvailability ? (
            <p className="text-white/80">
              {getDisplayLabel(data.travelAvailability, FILTER_LABELS.travelAvailability)}
            </p>
          ) : (
            <p className="text-red-400">Por favor, completar o perfil.</p>
          )}
        </div>

        <div>
          <h3 className="text-white font-medium text-lg mb-2">Estado civil</h3>
          {data.maritalStatus ? (
            <p className="text-white/80">
              {getDisplayLabel(data.maritalStatus, FILTER_LABELS.maritalStatus)}
            </p>
          ) : (
            <p className="text-red-400">Por favor, completar o perfil.</p>
          )}
        </div>

        <div>
          <h3 className="text-white font-medium text-lg mb-2">Deseja sigilo sobre sua presença na plataforma?</h3>
          {data.sigilo_presenca ? (
            <p className="text-white/80">
              {getDisplayLabel(data.sigilo_presenca, FILTER_LABELS.sigilo_presenca)}
            </p>
          ) : (
            <p className="text-red-400">Por favor, completar o perfil.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!isOnboarding && !isEditing && (
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 bg-luxury-red/20 rounded-full flex items-center justify-center">
            <span className="text-lg">💕</span>
          </div>
          <h3 className="text-white font-medium">Relacionamentos</h3>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="relationshipType" className={labelClass}>Tipo de conexão</Label>
          <Select value={data.relationshipType || ''} onValueChange={(value) => updateData('relationshipType', value)}>
            <SelectTrigger className={selectClass}>
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent className={contentClass}>
              {FILTER_OPTIONS.relationshipType.map(option => (
                <SelectItem 
                  key={option} 
                  value={option}
                  className={itemClass}
                >
                  {FILTER_LABELS.relationshipType[option as keyof typeof FILTER_LABELS.relationshipType]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="travelAvailability" className={labelClass}>Tem disponibilidade para viajar?</Label>
          <Select value={data.travelAvailability || ''} onValueChange={(value) => updateData('travelAvailability', value)}>
            <SelectTrigger className={selectClass}>
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent className={contentClass}>
              {FILTER_OPTIONS.travelAvailability.map(option => (
                <SelectItem 
                  key={option} 
                  value={option}
                  className={itemClass}
                >
                  {FILTER_LABELS.travelAvailability[option as keyof typeof FILTER_LABELS.travelAvailability]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="maritalStatus" className={labelClass}>Estado civil</Label>
          <Select value={data.maritalStatus || ''} onValueChange={(value) => updateData('maritalStatus', value)}>
            <SelectTrigger className={selectClass}>
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent className={contentClass}>
              {FILTER_OPTIONS.maritalStatus.map(option => (
                <SelectItem 
                  key={option} 
                  value={option}
                  className={itemClass}
                >
                  {FILTER_LABELS.maritalStatus[option as keyof typeof FILTER_LABELS.maritalStatus]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="sigilo_presenca" className={labelClass}>Deseja sigilo sobre sua presença na plataforma?</Label>
          <Select value={data.sigilo_presenca || ''} onValueChange={(value) => updateData('sigilo_presenca', value)}>
            <SelectTrigger className={selectClass}>
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent className={contentClass}>
              {FILTER_OPTIONS.sigilo_presenca.map(option => (
                <SelectItem 
                  key={option} 
                  value={option}
                  className={itemClass}
                >
                  {FILTER_LABELS.sigilo_presenca[option as keyof typeof FILTER_LABELS.sigilo_presenca]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

export default UpdatedRelationshipsSection;